import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { tr } from '../../../i18n/i18n';
import i18n from '../../../i18n/i18n';
import {
  IAdminStudent,
  IAdminStudentDetail,
  IGetAdminStudentsResponse,
  IGetAdminStudentByIdResponse,
  IUpdateAdminStudentResponse,
} from '../../../interface/classveew-admin/admin-management.interface';
import {
  fetchAdminStudents,
  fetchAdminStudentById,
  updateAdminStudentProfile,
} from '../../action/classveew-admin/admin-student.action';

interface IAdminStudentState {
  students: IAdminStudent[];
  selectedStudent: IAdminStudentDetail | null;
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  isLoading: boolean;
  error: string | null;
}

const initialState: IAdminStudentState = {
  students: [],
  selectedStudent: null,
  page: 1,
  pageSize: 20,
  total: 0,
  totalPages: 0,
  isLoading: false,
  error: null,
};

// Slice
const adminStudentSlice = createSlice({
  name: 'adminStudent',
  initialState,
  reducers: {
    clearAdminStudents: (state) => {
      state.students = [];
      state.page = 1;
      state.total = 0;
      state.totalPages = 0;
    },
    clearSelectedStudent: (state) => {
      state.selectedStudent = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all students
      .addCase(fetchAdminStudents.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchAdminStudents.fulfilled,
        (state, action: PayloadAction<IGetAdminStudentsResponse>) => {
          state.isLoading = false;
          state.students = action.payload.data?.students || [];
          state.page = action.payload.data?.page || 1;
          state.pageSize = action.payload.data?.pageSize || 20;
          state.total = action.payload.data?.total || 0;
          state.totalPages = action.payload.data?.totalPages || 0;
          state.error = null;
        },
      )
      .addCase(fetchAdminStudents.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || tr('g.failed_to_fetch_students', 'Failed to fetch students');
        state.students = [];
      })
      // Fetch student by ID
      .addCase(fetchAdminStudentById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchAdminStudentById.fulfilled,
        (state, action: PayloadAction<IGetAdminStudentByIdResponse>) => {
          state.isLoading = false;
          state.selectedStudent = action.payload.data?.student || null;
          state.error = null;
        },
      )
      .addCase(fetchAdminStudentById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || i18n.t('toast.failed_to_fetch_student', 'Failed to fetch student');
        state.selectedStudent = null;
      })
      // Update student
      .addCase(updateAdminStudentProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        updateAdminStudentProfile.fulfilled,
        (state, action: PayloadAction<IUpdateAdminStudentResponse>) => {
          state.isLoading = false;
          state.selectedStudent = action.payload.data?.student || null;
          // Update in list if present
          const index = state.students.findIndex((s) => s.id === action.payload.data?.student?.id);
          if (index !== -1 && action.payload.data?.student) {
            state.students[index] = {
              ...state.students[index],
              first_name: action.payload.data.student.first_name,
              last_name: action.payload.data.student.last_name,
              full_name: action.payload.data.student.full_name,
              status: action.payload.data.student.status,
            };
          }
          state.error = null;
        },
      )
      .addCase(updateAdminStudentProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || tr('g.failed_to_update_student', 'Failed to update student');
      });
  },
});

export const { clearAdminStudents, clearSelectedStudent } = adminStudentSlice.actions;
export default adminStudentSlice.reducer;
